import {useCallback, useEffect, useState} from "react";
import {css} from "@panda/css";

import {prettyScroll} from "../../../../styles/receipes/pretty_scroll";
import {OpenStreetMapsWithPoi} from "../../../maps/components/map_with_poi/OpenStreetMapsWithPoi";
import {PoiSwitcher} from "../../../maps/components/PoiSwitcher";
import {SinglePoi} from "../../../maps/components/SinglePoi";
import {POI_DISTANCE_DEFAULT_VALUE} from "../../../maps/constants/pois_defaults";
import {useMapLayersAppliedTracking} from "../../../maps/hooks/use_map_layers_applied_tracking";
import {useMapNearbyOffersPois} from "../../../maps/hooks/use_map_nearby_offers_pois";
import {useSinglePoiMarker} from "../../../maps/hooks/use_single_poi_marker";
import {clearPendingSinglePoi} from "../../../maps/state/pending_single_poi_slice";
import {AmplitudeMapLayersLocation} from "../../../maps/tracking/track_amplitude_map_layers_applied";
import {trackAmplitudeMapPoiClick} from "../../../maps/tracking/track_amplitude_poi_click";
import {PoiType} from "../../../maps/types/PoiType";
import {PolygonType} from "../../../maps/types/PolygonType";
import {OsmPoiClickHandler} from "../../../maps/utils/create_get_osm_poi_marker";
import {getHasCountryPois} from "../../../maps/utils/get_has_country_pois";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {getAmplitudeOfferPageLocation} from "../../../tracking/amplitude/get_amplitude_offer_page_location";
import {trackAmplitudeButtonClick} from "../../../tracking/amplitude/track_amplitude_button_click";
import {ViewType} from "../../../view_type/ViewType";
import {AreaPhotosView} from "../../components/area_photos/AreaPhotosView";
import {IOfferDetail} from "../../types/IOfferDetail";
import {getHasAreaPhotos} from "../../utils/get_has_area_photos";
import {getOfferBuildingPolygonOptions} from "../constants/offer_building_polygon_properties";
import {IOfferBuilding} from "../types/IOfferBuilding";

interface IProps {
    area?: {
        center: {
            coordinates: [number, number];
        };
        polygon: {
            coordinates: number[][][];
        };
        region: number;
    };
    offer: IOfferDetail | IPropertyDetailOffer;
    property?: {
        id: number;
        for_sale: boolean;
        building?: number;
    };
    offerBuildings?: IOfferBuilding[];
}

export const OfferMapModalContent = (props: IProps) => {
    const [poiDistance, setPoiDistance] = useState<number>(POI_DISTANCE_DEFAULT_VALUE);

    const dispatch = useAppDispatch();
    const pendingSinglePoi = useAppSelector((state) => state.pendingSinglePoi.poi);
    const {markers: userMarkers, onSinglePoiChange} = useSinglePoiMarker(props.offer);

    const amplitudePropertyId = props.property?.id ?? null;
    const amplitudeLocation = getAmplitudeOfferPageLocation(amplitudePropertyId);

    useEffect(() => {
        if (pendingSinglePoi) {
            onSinglePoiChange(pendingSinglePoi);
            dispatch(clearPendingSinglePoi());
        }
    }, [pendingSinglePoi]);

    const {offerAdditionalPois, defaultPoiTypes, mapBottomSlot} = useMapNearbyOffersPois({
        baseOffer: {
            id: props.offer.id,
            type: props.offer.type,
            stats: {
                ranges_area_min: props.offer.stats?.ranges_area_min,
                ranges_area_max: props.offer.stats?.ranges_area_max,
                rooms: props.offer.stats?.rooms
            }
        },
        distance: poiDistance,
        showPropertyNumberOnMarker: !!props.property
    });

    const hasPoiSwitch = getHasCountryPois(props.offer.region.country);

    const createOfferBuildingsPolygonList = () => {
        if (props.offerBuildings) {
            return props.offerBuildings.map(({geo_area, name, floors_count, id}) => ({
                coords: [geo_area.coordinates],
                options: getOfferBuildingPolygonOptions(id, props.property?.building),
                infoWindow: {
                    content: name
                },
                type: PolygonType.BLOCK,
                height: floors_count
            }));
        }
    };

    const onPoiClick = useCallback<OsmPoiClickHandler>(
        (poi, poiType, poiSubType) => {
            trackAmplitudeMapPoiClick({
                location: amplitudeLocation,
                poi,
                poiType,
                poiSubType,
                itemId: props.offer.id,
                propertyId: amplitudePropertyId
            });
        },
        [props.offer.id, props.property?.id]
    );

    const onCheckTravelTime = useCallback(() => {
        trackAmplitudeButtonClick({
            buttonName: "check_travel_time",
            buttonCategory: amplitudeLocation,
            itemId: props.offer.id,
            propertyId: amplitudePropertyId,
            buttonLink: null,
            value: "check_travel_time"
        });
    }, [props.offer.id, props.property?.id]);

    return (
        <div className={mapModalHolderStyle}>
            <AreaPhotosView
                offer={props.offer}
                propertyId={amplitudePropertyId}
                hasAreaPhotos={getHasAreaPhotos(props.offer)}
                placement="fullscreen_map"
                isMapBottomSlotOpen={Boolean(mapBottomSlot)}
            >
                <OpenStreetMapsWithPoi
                    offer={props.offer}
                    viewType={props.property ? ViewType.PROPERTY : ViewType.OFFER}
                    polygon={props.area && props.area.polygon}
                    detailPolygons={createOfferBuildingsPolygonList()}
                    region={props.area && props.area.region}
                    disableInitiallyOpenedPoiId
                    mapConfig={{fitBounds: true}}
                    clusterMarkers
                    customMarkers={userMarkers}
                    customPoiMarkers={offerAdditionalPois}
                    disablePoiSwitch={!hasPoiSwitch}
                    initialPoiTypes={defaultPoiTypes}
                    mapBottomSlot={mapBottomSlot}
                    className={mapCustomStylesStyle}
                    onPoiClick={onPoiClick}
                    drawPoiDistance
                    onDistanceChange={setPoiDistance}
                    onCheckTravelTime={onCheckTravelTime}
                >
                    {({checkedPoiTypes, setCheckedPoiTypes, poiDistance: mapPoiDistance, setPoiDistance: onMapDistanceChange, map}) => (
                        <>
                            <OfferMapModalContentLayersTracker
                                checkedPoiTypes={checkedPoiTypes}
                                poiDistance={mapPoiDistance}
                                location={amplitudeLocation}
                                itemId={props.offer.id}
                                propertyId={amplitudePropertyId}
                            />

                            {hasPoiSwitch ? (
                                <div className={mapPlacesPositionStyle}>
                                    <div className={mapPlacesContentStyle}>
                                        <PoiSwitcher
                                            onChange={setCheckedPoiTypes}
                                            checkedPoiTypes={checkedPoiTypes}
                                            distanceValue={mapPoiDistance}
                                            onDistanceChange={onMapDistanceChange}
                                            disableCollapsible
                                        />

                                        <SinglePoi offer={props.offer} onChange={onSinglePoiChange} onCheckTravelTime={onCheckTravelTime} disableCollapsible />
                                    </div>
                                </div>
                            ) : null}

                            {map}
                        </>
                    )}
                </OpenStreetMapsWithPoi>
            </AreaPhotosView>
        </div>
    );
};

interface ILayersTrackerProps {
    checkedPoiTypes: PoiType[];
    poiDistance: number;
    location: AmplitudeMapLayersLocation;
    itemId: number;
    propertyId: number | null;
}

const OfferMapModalContentLayersTracker = (props: ILayersTrackerProps) => {
    useMapLayersAppliedTracking(props);

    return null;
};

const mapModalHolderStyle = css({
    flexGrow: 1,
    display: "flex",
    flexDirection: "row",
    pr: 0,
    position: "relative"
});

const mapCustomStylesStyle = css({
    "& .leaflet-div-icon": {
        backgroundColor: "transparent !important",
        border: "none !important"
    }
});

const mapPlacesPositionStyle = css({
    position: "absolute",
    top: "0.8rem",
    left: "0.8rem",
    zIndex: 10,
    overflow: "hidden",
    display: "none",

    md: {
        display: "flex",
        backgroundColor: "#fff",
        width: "33rem",
        boxShadow: "var(--shadow-1)",
        borderRadius: 2
    }
});

const mapPlacesContentStyle = css({
    ...prettyScroll,
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    width: "100%",
    height: "100%",
    p: 2
});
import {useCallback, useEffect, useState} from "react";
import {css} from "@panda/css";

import {prettyScroll} from "../../../../styles/receipes/pretty_scroll";
import {OpenStreetMapsWithPoi} from "../../../maps/components/map_with_poi/OpenStreetMapsWithPoi";
import {PoiSwitcher} from "../../../maps/components/PoiSwitcher";
import {SinglePoi} from "../../../maps/components/SinglePoi";
import {POI_DISTANCE_DEFAULT_VALUE} from "../../../maps/constants/pois_defaults";
import {useMapLayersAppliedTracking} from "../../../maps/hooks/use_map_layers_applied_tracking";
import {useMapNearbyOffersPois} from "../../../maps/hooks/use_map_nearby_offers_pois";
import {useSinglePoiMarker} from "../../../maps/hooks/use_single_poi_marker";
import {clearPendingSinglePoi} from "../../../maps/state/pending_single_poi_slice";
import {AmplitudeMapLayersLocation} from "../../../maps/tracking/track_amplitude_map_layers_applied";
import {trackAmplitudeMapPoiClick} from "../../../maps/tracking/track_amplitude_poi_click";
import {PoiType} from "../../../maps/types/PoiType";
import {PolygonType} from "../../../maps/types/PolygonType";
import {OsmPoiClickHandler} from "../../../maps/utils/create_get_osm_poi_marker";
import {getHasCountryPois} from "../../../maps/utils/get_has_country_pois";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {getAmplitudeOfferPageLocation} from "../../../tracking/amplitude/get_amplitude_offer_page_location";
import {trackAmplitudeButtonClick} from "../../../tracking/amplitude/track_amplitude_button_click";
import {ViewType} from "../../../view_type/ViewType";
import {AreaPhotosView} from "../../components/area_photos/AreaPhotosView";
import {IOfferDetail} from "../../types/IOfferDetail";
import {getHasAreaPhotos} from "../../utils/get_has_area_photos";
import {getOfferBuildingPolygonOptions} from "../constants/offer_building_polygon_properties";
import {IOfferBuilding} from "../types/IOfferBuilding";

interface IProps {
    area?: {
        center: {
            coordinates: [number, number];
        };
        polygon: {
            coordinates: number[][][];
        };
        region: number;
    };
    offer: IOfferDetail | IPropertyDetailOffer;
    property?: {
        id: number;
        for_sale: boolean;
        building?: number;
    };
    offerBuildings?: IOfferBuilding[];
}

export const OfferMapModalContent = (props: IProps) => {
    const [poiDistance, setPoiDistance] = useState<number>(POI_DISTANCE_DEFAULT_VALUE);

    const dispatch = useAppDispatch();
    const pendingSinglePoi = useAppSelector((state) => state.pendingSinglePoi.poi);
    const {markers: userMarkers, onSinglePoiChange} = useSinglePoiMarker(props.offer);

    const amplitudePropertyId = props.property?.id ?? null;
    const amplitudeLocation = getAmplitudeOfferPageLocation(amplitudePropertyId);

    useEffect(() => {
        if (pendingSinglePoi) {
            onSinglePoiChange(pendingSinglePoi);
            dispatch(clearPendingSinglePoi());
        }
    }, [pendingSinglePoi]);

    const {offerAdditionalPois, defaultPoiTypes, mapBottomSlot} = useMapNearbyOffersPois({
        baseOffer: {
            id: props.offer.id,
            type: props.offer.type,
            stats: {
                ranges_area_min: props.offer.stats?.ranges_area_min,
                ranges_area_max: props.offer.stats?.ranges_area_max,
                rooms: props.offer.stats?.rooms
            }
        },
        distance: poiDistance,
        showPropertyNumberOnMarker: !!props.property
    });

    const hasPoiSwitch = getHasCountryPois(props.offer.region.country);

    const createOfferBuildingsPolygonList = () => {
        if (props.offerBuildings) {
            return props.offerBuildings.map(({geo_area, name, floors_count, id}) => ({
                coords: [geo_area.coordinates],
                options: getOfferBuildingPolygonOptions(id, props.property?.building),
                infoWindow: {
                    content: name
                },
                type: PolygonType.BLOCK,
                height: floors_count
            }));
        }
    };

    const onPoiClick = useCallback<OsmPoiClickHandler>(
        (poi, poiType, poiSubType) => {
            trackAmplitudeMapPoiClick({
                location: amplitudeLocation,
                poi,
                poiType,
                poiSubType,
                itemId: props.offer.id,
                propertyId: amplitudePropertyId
            });
        },
        [props.offer.id, props.property?.id]
    );

    const onCheckTravelTime = useCallback(() => {
        trackAmplitudeButtonClick({
            buttonName: "check_travel_time",
            buttonCategory: amplitudeLocation,
            itemId: props.offer.id,
            propertyId: amplitudePropertyId,
            buttonLink: null,
            value: "check_travel_time"
        });
    }, [props.offer.id, props.property?.id]);

    return (
        <div className={mapModalHolderStyle}>
            <AreaPhotosView
                offer={props.offer}
                propertyId={amplitudePropertyId}
                hasAreaPhotos={getHasAreaPhotos(props.offer)}
                placement="fullscreen_map"
                isMapBottomSlotOpen={Boolean(mapBottomSlot)}
            >
                <OpenStreetMapsWithPoi
                    offer={props.offer}
                    viewType={props.property ? ViewType.PROPERTY : ViewType.OFFER}
                    polygon={props.area && props.area.polygon}
                    detailPolygons={createOfferBuildingsPolygonList()}
                    region={props.area && props.area.region}
                    disableInitiallyOpenedPoiId
                    mapConfig={{fitBounds: true}}
                    clusterMarkers
                    customMarkers={userMarkers}
                    customPoiMarkers={offerAdditionalPois}
                    disablePoiSwitch={!hasPoiSwitch}
                    initialPoiTypes={defaultPoiTypes}
                    mapBottomSlot={mapBottomSlot}
                    className={mapCustomStylesStyle}
                    onPoiClick={onPoiClick}
                    drawPoiDistance
                    onDistanceChange={setPoiDistance}
                    onCheckTravelTime={onCheckTravelTime}
                >
                    {({checkedPoiTypes, setCheckedPoiTypes, poiDistance: mapPoiDistance, setPoiDistance: onMapDistanceChange, map}) => (
                        <>
                            <OfferMapModalContentLayersTracker
                                checkedPoiTypes={checkedPoiTypes}
                                poiDistance={mapPoiDistance}
                                location={amplitudeLocation}
                                itemId={props.offer.id}
                                propertyId={amplitudePropertyId}
                            />

                            {hasPoiSwitch ? (
                                <div className={mapPlacesPositionStyle}>
                                    <div className={mapPlacesContentStyle}>
                                        <PoiSwitcher
                                            onChange={setCheckedPoiTypes}
                                            checkedPoiTypes={checkedPoiTypes}
                                            distanceValue={mapPoiDistance}
                                            onDistanceChange={onMapDistanceChange}
                                            disableCollapsible
                                        />

                                        <SinglePoi offer={props.offer} onChange={onSinglePoiChange} onCheckTravelTime={onCheckTravelTime} disableCollapsible />
                                    </div>
                                </div>
                            ) : null}

                            {map}
                        </>
                    )}
                </OpenStreetMapsWithPoi>
            </AreaPhotosView>
        </div>
    );
};

interface ILayersTrackerProps {
    checkedPoiTypes: PoiType[];
    poiDistance: number;
    location: AmplitudeMapLayersLocation;
    itemId: number;
    propertyId: number | null;
}

const OfferMapModalContentLayersTracker = (props: ILayersTrackerProps) => {
    useMapLayersAppliedTracking(props);

    return null;
};

const mapModalHolderStyle = css({
    flexGrow: 1,
    display: "flex",
    flexDirection: "row",
    pr: 0,
    position: "relative"
});

const mapCustomStylesStyle = css({
    "& .leaflet-div-icon": {
        backgroundColor: "transparent !important",
        border: "none !important"
    }
});

const mapPlacesPositionStyle = css({
    position: "absolute",
    top: "0.8rem",
    left: "0.8rem",
    zIndex: 10,
    overflow: "hidden",
    display: "none",

    md: {
        display: "flex",
        backgroundColor: "#fff",
        width: "33rem",
        boxShadow: "var(--shadow-1)",
        borderRadius: 2
    }
});

const mapPlacesContentStyle = css({
    ...prettyScroll,
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    width: "100%",
    height: "100%",
    p: 2
});
